import { useCallback, useEffect, useRef } from 'react';
import {
  getTrimmedAudioUrl,
  prefetchTrimmedAudio,
} from '@src/services/trimmedAudio';

// Two elements, used alternately. Blocks in a dubbing project often butt up
// against each other, and a clip is heard slightly later than the clock reaches
// it (loading the src, then `play()` actually starting). Its tail therefore
// runs a little past `endTime`. With ONE element, pointing it at the next
// block's clip would cut that tail off — the lost-word symptom this whole change
// exists to remove. Alternating lets the outgoing clip finish itself while the
// incoming one starts; the overlap is the few tens of ms of start latency, and
// the clip's built-in fade-out makes it a soft one.
const PLAYER_COUNT = 2;

// Grace given to a clip after the clock leaves its block, before it is stopped.
// Only the raw-file fallback ever reaches it: an exactly-cut clip has already
// run out of file by then.
const LEAVE_GRACE_MS = 300;

/**
 * Plays one dubbing block's voice clip at a time, cut to the block's slot.
 *
 * The trim lives in the SOURCE — `services/trimmedAudio` hands back a clip whose
 * length already is the played window — so nothing here ever has to stop a clip
 * at the right instant. That is what makes the end exact: the element runs out
 * of file, which no amount of main-thread jank or output latency can shift.
 *
 * Falls back to the raw file when an exact clip cannot be prepared (no Web
 * Audio, CORS, unsupported codec, over the size caps). The caller's coarse
 * out-of-slot backstop still bounds that case.
 */
export const useVoiceClipPlayer = ({ enabled = false } = {}) => {
  const playersRef = useRef(null);
  const activeIndexRef = useRef(0);
  // Block whose clip the active element holds; null once the clock has left it.
  const activeBlockIdRef = useRef(null);
  // Bumped on every load so a source that resolves late cannot hijack the
  // element from a block the clock has since moved on to.
  const loadTokenRef = useRef(0);
  // A load is in flight — the element is legitimately paused, so the per-tick
  // resume must not fight it.
  const loadingRef = useRef(false);
  const volumeRef = useRef(1);

  if (playersRef.current === null) {
    playersRef.current = Array.from(
      { length: PLAYER_COUNT },
      () => new Audio(),
    );
  }

  const players = playersRef.current;
  // Always points at the element currently owning playback, so callers that
  // need the media element itself (the legacy video sync) keep a stable handle.
  const elementRef = useRef(players[0]);
  const activeElement = useCallback(
    () => players[activeIndexRef.current],
    [players],
  );
  // Per element: does it hold an exactly-cut clip, or the raw-file fallback?
  const isExactRef = useRef(Array(PLAYER_COUNT).fill(false));
  // Bounds how long a clip may run past the slot the clock has already left.
  // An exactly-cut clip ends on its own well inside this; the raw-file fallback
  // would otherwise run to the end of a file many times longer than its block.
  const leaveTimerRef = useRef(null);

  /**
   * Point an element at `audioLink` cut to `slotSec` and start it at
   * `offsetSec`. Resolving the clip is asynchronous, so the token guard drops
   * the result if the clock has moved on meanwhile.
   */
  const loadInto = useCallback(
    async (index, { audioLink, slotSec, offsetSec, playing }) => {
      loadTokenRef.current += 1;
      const token = loadTokenRef.current;
      loadingRef.current = true;
      const clipUrl = await getTrimmedAudioUrl(audioLink, slotSec);
      if (token !== loadTokenRef.current) return;
      loadingRef.current = false;

      const element = players[index];
      // Whether this element holds a clip whose LENGTH is the played window.
      // A fallback holds the raw file — many times longer than the block — so it
      // must never be left running the way an exact clip can be.
      isExactRef.current[index] = Boolean(clipUrl);
      // eslint-disable-next-line no-param-reassign
      element.src = clipUrl || audioLink;
      // eslint-disable-next-line no-param-reassign
      element.volume = volumeRef.current;
      if (offsetSec > 0) {
        // eslint-disable-next-line no-param-reassign
        element.currentTime = offsetSec;
      }
      if (playing) element.play().catch(() => {});
    },
    [players],
  );

  /**
   * The clock is inside `block`. On entry (or an explicit seek) this loads the
   * block's clip and starts it; on the ticks that follow it only keeps playback
   * alive, never repositioning — a clip must run at its own pace, not be nudged
   * back to where a 100ms clock thinks it should be.
   *
   * `offsetSec` is 0 for a block the clock simply reached: the clip has to start
   * at its first sample. Only a deliberate seek into the middle of a block
   * passes a non-zero offset.
   */
  const enterBlock = useCallback(
    ({
      blockId,
      audioLink,
      slotSec,
      offsetSec = 0,
      playing,
      force = false,
    }) => {
      if (leaveTimerRef.current) {
        clearTimeout(leaveTimerRef.current);
        leaveTimerRef.current = null;
      }
      const isSameBlock = activeBlockIdRef.current === blockId;
      if (isSameBlock && !force) {
        const element = activeElement();
        if (
          playing &&
          element.paused &&
          !element.ended &&
          !loadingRef.current
        ) {
          element.play().catch(() => {});
        }
        return;
      }

      // A new block takes the OTHER element so the outgoing clip keeps its tail.
      // A forced reload of the same block reuses its own element.
      if (!isSameBlock) {
        const outgoing = activeElement();
        const outgoingWasExact = isExactRef.current[activeIndexRef.current];
        activeIndexRef.current = (activeIndexRef.current + 1) % PLAYER_COUNT;
        // Letting the outgoing clip finish is safe ONLY for an exact clip: its
        // length is its own slot, so it runs out within tens of ms. A fallback
        // holds the whole file — 13-46s measured against a 2s block — and would
        // talk over everything after it, so it stops here instead.
        if (!outgoingWasExact) outgoing.pause();
      }
      activeBlockIdRef.current = blockId;
      const element = activeElement();
      elementRef.current = element;
      // Letting the outgoing clip finish is right only when the clock ROLLED from
      // one block into the next — it is that block's own tail, a few tens of ms.
      // A jump (user seek, block click) is not that: whatever was playing belongs
      // to a part of the timeline the user just left, so everything stops first.
      // Otherwise seeking back from block 2 to block 1 leaves both sounding.
      if (force) players.forEach((player) => player.pause());
      else element.pause();
      loadInto(activeIndexRef.current, {
        audioLink,
        slotSec,
        offsetSec,
        playing,
      });
    },
    [activeElement, loadInto, players],
  );

  /**
   * The clock has left every block with audio. The active clip is NOT stopped:
   * it is already exactly the right length and is simply finishing the tail it
   * started late. Only forget it, so coming back counts as a fresh entry.
   */
  const leaveBlock = useCallback(() => {
    if (activeBlockIdRef.current === null) return;
    activeBlockIdRef.current = null;
    const element = activeElement();
    if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
    leaveTimerRef.current = setTimeout(() => {
      leaveTimerRef.current = null;
      if (!element.paused) element.pause();
    }, LEAVE_GRACE_MS);
  }, [activeElement]);

  /** Hard stop — transport paused, seeked, reset or unmounted. */
  const stopAll = useCallback(() => {
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
    players.forEach((element) => {
      element.pause();
    });
    activeBlockIdRef.current = null;
    loadTokenRef.current += 1;
    loadingRef.current = false;
    isExactRef.current = Array(PLAYER_COUNT).fill(false);
  }, [players]);

  /** Pause both elements without forgetting the active block (transport pause). */
  const pauseAll = useCallback(() => {
    players.forEach((element) => {
      element.pause();
    });
  }, [players]);

  const setVolume = useCallback(
    (value) => {
      volumeRef.current = Math.max(0, Math.min(1, value));
      players.forEach((element) => {
        // eslint-disable-next-line no-param-reassign
        element.volume = volumeRef.current;
      });
    },
    [players],
  );

  /** Prepare an upcoming block's clip so entering it pays no decode. */
  const prefetch = useCallback((audioLink, slotSec) => {
    if (audioLink && slotSec > 0) prefetchTrimmedAudio(audioLink, slotSec);
  }, []);

  useEffect(() => {
    if (enabled) return undefined;
    stopAll();
    return undefined;
  }, [enabled, stopAll]);

  useEffect(
    () => () => {
      if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
      players.forEach((element) => {
        element.pause();
        element.removeAttribute('src');
      });
    },
    [players],
  );

  return {
    elementRef,
    enterBlock,
    leaveBlock,
    stopAll,
    pauseAll,
    setVolume,
    prefetch,
  };
};

export default useVoiceClipPlayer;
